/*
 * Contact form acknowledgement.
 * -----------------------------------------------------------------------------
 * Sends a Klaviyo "Contact Form Submitted" event so a transactional flow can
 * acknowledge the sender. Shopify's native {% form 'contact' %} emails the store
 * and sends the submitter nothing, and Shopify has no native way to send a
 * transactional email to an address that has not opted into marketing:
 * Flow's "Send internal email" cannot be addressed from a variable, and the only
 * customer-facing native path is a marketing automation, which is consent-gated.
 *
 * Loaded only from sections/contact.liquid, so it is present on the three form
 * pages and nowhere else.
 *
 * WHY IT FIRES ON THE SUCCESS PAGE, NOT ON SUBMIT
 *
 * The fields are stashed when the form is submitted, but the event is only sent
 * after the reload, and only if the server rendered [data-contact-posted]. That
 * means:
 *   - we never acknowledge a submission Shopify rejected on validation;
 *   - we never put a network call in front of the customer's actual message;
 *   - we inherit the native form's own spam handling instead of bypassing it.
 *
 * WHAT IS DELIBERATELY NOT SENT
 *
 * The message body, company and telephone stay in Shopify's concierge
 * notification and are never sent here. Beyond data minimisation, echoing free
 * text back would let someone submit a third party's address with crafted body
 * copy and have Lunburg's authenticated sending domain deliver it for them. The
 * name is sent because the greeting needs it, and is only ever written to the
 * page via textContent.
 *
 * IDEMPOTENCY
 *
 * A reference is generated before the POST and sent as unique_id. Klaviyo
 * deduplicates on profile + metric + unique_id, so a reload of the success page
 * cannot produce a second acknowledgement. The stash is cleared only on a 2xx;
 * a failure leaves it for the next page load, bounded by MAX_ATTEMPTS.
 */
(function () {
  if (window.__lunburgContactAck) return;
  window.__lunburgContactAck = true;

  var COMPANY_ID = 'TJ3xFH';
  var REVISION = '2026-04-15';
  var EVENT_NAME = 'Contact Form Submitted';
  var STORE_KEY = 'lb_contact_ack_pending';
  var MAX_ATTEMPTS = 3;

  /* sessionStorage throws in some privacy modes; a failure here must never break
     the form itself, only the acknowledgement. */
  function readPending() {
    try {
      var raw = sessionStorage.getItem(STORE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function writePending(obj) {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(obj)); } catch (e) {}
  }

  function clearPending() {
    try { sessionStorage.removeItem(STORE_KEY); } catch (e) {}
  }

  function reference() {
    var hex = '';
    try {
      var buf = new Uint8Array(4);
      window.crypto.getRandomValues(buf);
      for (var i = 0; i < buf.length; i++) {
        hex += ('0' + buf[i].toString(16)).slice(-2);
      }
    } catch (e) {
      hex = Math.random().toString(16).slice(2, 10);
    }
    return 'LB-' + hex.toUpperCase();
  }

  function fieldValue(form, name) {
    var el = form.querySelector('[name="' + name + '"]');
    return el && el.value ? el.value.trim() : '';
  }

  /* ---------------------------------------------------------------- submit -- */

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.nodeName !== 'FORM') return;
    if (!form.querySelector('input[name="form_type"][value="contact"]')) return;

    var email = fieldValue(form, 'contact[email]');
    if (!email) return;

    var name = fieldValue(form, 'contact[name]');
    var space = name.indexOf(' ');
    var wrap = form.closest('[data-contact-form-name]');

    writePending({
      reference: reference(),
      email: email,
      name: name,
      first_name: space > 0 ? name.slice(0, space) : name,
      last_name: space > 0 ? name.slice(space + 1) : '',
      form_name: (wrap && wrap.getAttribute('data-contact-form-name')) || 'Contact',
      inquiry_type: fieldValue(form, 'contact[Inquiry type]'),
      source_page: window.location.pathname,
      submitted_at: new Date().toISOString(),
      attempts: 0
    });
  });

  /* ------------------------------------------------------- confirmed success -- */

  var posted = document.querySelector('[data-contact-posted]');
  if (!posted) return;

  var pending = readPending();
  if (!pending || !pending.email) return;

  if ((pending.attempts || 0) >= MAX_ATTEMPTS) {
    clearPending();
    return;
  }

  pending.attempts = (pending.attempts || 0) + 1;
  writePending(pending);

  var payload = {
    data: {
      type: 'event',
      attributes: {
        unique_id: pending.reference,
        metric: { data: { type: 'metric', attributes: { name: EVENT_NAME } } },
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email: pending.email,
              first_name: pending.first_name,
              last_name: pending.last_name
            }
          }
        },
        properties: {
          form_name: pending.form_name,
          inquiry_type: pending.inquiry_type || pending.form_name,
          submission_reference: pending.reference,
          source_page: pending.source_page,
          submitted_at: pending.submitted_at
        }
      }
    }
  };

  fetch('https://a.klaviyo.com/client/events/?company_id=' + COMPANY_ID, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', revision: REVISION },
    body: JSON.stringify(payload)
  }).then(function (res) {
    if (!res || !res.ok) return;

    clearPending();

    /* Only now is the receipt line true, so only now is it shown. textContent,
       never innerHTML: the address is visitor-supplied. */
    var receipt = posted.querySelector('[data-contact-receipt]');
    if (receipt) {
      receipt.textContent =
        receipt.dataset.receiptTemplate.replace('__email__', function () { return pending.email; })
          .replace('__reference__', function () { return pending.reference; });
      receipt.hidden = false;
    }
  }).catch(function () {
    /* Left in the stash for the next load. The message already reached the
       concierge; only the receipt is delayed. */
  });
})();
